import React from 'react';
import { Card, CardContent, CardActions, Box, Typography, Button, Chip } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { FormattedMessage } from 'react-intl';
import { ThemeMode } from 'config';
/**
 * Custom Tooltip Component for react-joyride tour
 *
 * @param {Object} props - Props from react-joyride
 * @param {Object} props.step - Current step data
 * @param {Object} props.primaryProps - Props for the primary (Next) button
 * @param {Object} props.skipProps - Props for the Skip button
 * @param {Object} props.tooltipProps - Props for the tooltip container
 * @param {number} props.index - Current step index (0-based)
 * @param {number} props.size - Total number of steps
 */
export default function TourTooltip({ step, primaryProps, skipProps, backProps, tooltipProps, index, size }) {
  const { title, description, image, leftImage, width = '520px', imageHeight = '230px', isNew, background = 'background.paper' } = step;
  const isLastStep = index === size - 1;
  const theme = useTheme();
  return (
    <Box {...tooltipProps}>
      <Card
        sx={{
          width: width,
          borderRadius: 2.2,
          boxShadow: 'none',
          p: 0,
          backgroundColor: background
        }}
      >
        <CardContent sx={{ p: 3 }}>
          {/* Illustration */}
          {image && (
            <Box
              sx={{
                height: imageHeight,
                bgcolor: theme.palette.mode === ThemeMode.DARK ? alpha(theme.palette.secondary.main, 0.1) : 'bluegrey.light',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2.5,
                p: 1.5
              }}
            >
              <Box component="img" src={image} alt={title || 'Tour step'} sx={{ maxHeight: '100%', maxWidth: '100%' }} />
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 3 }}>
            {/* Left card image */}
            {leftImage && (
              <Box
                sx={{
                  minWidth: 218,
                  bgcolor: theme.palette.mode === ThemeMode.DARK ? alpha(theme.palette.secondary.main, 0.1) : 'bluegrey.light',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2.5,
                  p: 1.5
                }}
              >
                <Box component="img" src={leftImage} alt={title || 'Tour step'} sx={{ maxHeight: '100%', maxWidth: '100%' }} />
              </Box>
            )}
            <Box>
              {isNew && (
                <Chip
                  label="NEW"
                  size="small"
                  sx={{
                    mb: 1,
                    backgroundColor: '#26E880',
                    color: '#2c3348',
                    borderRadius: '8px'
                  }}
                />
              )}
              <Typography variant="h2" fontWeight={500} mb={1.5} color="grey.800">
                {title || 'Tour Step'}
              </Typography>
              <Typography variant="body1" color="grey.800">
                {description || step.content}
              </Typography>
            </Box>
          </Box>
        </CardContent>

        <CardActions
          sx={{
            justifyContent: 'flex-end',
            px: 3,
            py: 2.5,
            borderTop: '1px solid',
            borderColor: theme.palette.mode === ThemeMode.DARK ? alpha(theme.palette.divider, 1) : alpha(theme.palette.divider, 0.5)
          }}
        >
          {index > 0 && backProps && (
            <Button variant="outlined" size="small" {...backProps}>
              <FormattedMessage id="back" defaultMessage="Back" />
            </Button>
          )}
          {skipProps && (
            <Button variant="outlined" {...skipProps} size="small">
              <FormattedMessage id="skip" defaultMessage="Skip" />
            </Button>
          )}
          <Button variant="contained" size="small" {...primaryProps}>
            {isLastStep ? <FormattedMessage id="finish" defaultMessage="Finish" /> : <FormattedMessage id="next" defaultMessage="Next" />}
          </Button>
        </CardActions>
      </Card>
    </Box>
  );
}
